import React, { useState } from 'react';
import { Trash2Icon, XIcon } from 'lucide-react';
import { documentOptions } from '../../data/documents';
import { FlagRecord, ProposedActionType, SignOffAnswer } from '../../types/assessment';
interface Props {
  rowId: string;
  rowLabel: string;
  kind: 'flag' | 'correction';
  existing?: FlagRecord;
  onSave: (record: FlagRecord) => void;
  onDelete: () => void;
  onClose: () => void;
}
const ACTIONS: {
  id: ProposedActionType;
  label: string;
  hint: string;
}[] = [{
  id: 'adjusting',
  label: 'Adjusting entry',
  hint: 'Dr / Cr / amount / narrative'
}, {
  id: 'reclass',
  label: 'Reclassification',
  hint: 'Dr / Cr / amount / narrative'
}, {
  id: 'escalate',
  label: 'Escalate',
  hint: 'To whom, and why'
}, {
  id: 'note',
  label: 'Note only',
  hint: 'Record the observation, take no action'
}];
const SIGN_OFFS: {
  id: SignOffAnswer;
  label: string;
}[] = [{
  id: 'yes',
  label: 'Yes'
}, {
  id: 'no',
  label: 'No'
}, {
  id: 'escalate',
  label: 'Escalate'
}];
export function FlagDrawer({
  rowId,
  rowLabel,
  kind,
  existing,
  onSave,
  onDelete,
  onClose
}: Props) {
  const [what, setWhat] = useState(existing?.what || '');
  const [sources, setSources] = useState<string[]>(existing?.sources || []);
  const [actionType, setActionType] = useState<ProposedActionType>(existing?.actionType || (kind === 'correction' ? 'adjusting' : 'note'));
  const [debit, setDebit] = useState(existing?.debit || '');
  const [credit, setCredit] = useState(existing?.credit || '');
  const [amount, setAmount] = useState(existing?.amount || '');
  const [narrative, setNarrative] = useState(existing?.narrative || '');
  const [escalateTo, setEscalateTo] = useState(existing?.escalateTo || '');
  const [escalateWhy, setEscalateWhy] = useState(existing?.escalateWhy || '');
  const [signOff, setSignOff] = useState<SignOffAnswer | null>(existing?.signOff || null);
  const needsEntry = actionType === 'adjusting' || actionType === 'reclass';
  const canSave = what.trim().length > 0 && signOff !== null;
  const toggleSource = (id: string) => setSources(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  const save = () => {
    if (!canSave || !signOff) return;
    onSave({
      rowId,
      kind: needsEntry ? 'correction' : 'flag',
      what: what.trim(),
      sources,
      actionType,
      debit: debit.trim(),
      credit: credit.trim(),
      amount: amount.trim(),
      narrative: narrative.trim(),
      escalateTo: escalateTo.trim(),
      escalateWhy: escalateWhy.trim(),
      signOff,
      at: Date.now()
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Entry for ${rowId}`} data-id="element-208" data-mp="3u3837ekvhoj7">
      <button type="button" aria-label="Close" onClick={onClose} className="flex-1 bg-slate-900/30" data-id="element-209" data-mp="2vdolzllcqts" />
      <aside className="flex h-full w-[540px] flex-col border-l border-slate-200 bg-white shadow-2xl" data-id="element-210" data-mp="2ipw7xs36ie8n">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-4" data-id="element-211" data-mp="2nk9x3cr8zp4v">
          <div data-id="element-212" data-mp="15queplnzw7xp">
            <p className="font-mono-num text-xs text-slate-500" data-id="element-213" data-mp="10q6miwo15l1w">{rowId}</p>
            <h2 className="mt-0.5 text-[15px] font-semibold text-slate-900" data-id="element-214" data-mp="10s117t8pkgxj">{rowLabel}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close drawer" className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-215" data-mp="1end2ykla77ny">
            <XIcon className="h-4 w-4" data-id="element-216" data-mp="i086nudzqnnp" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5" data-id="element-217" data-mp="vqld1sb8huax">
          <div data-id="element-218" data-mp="vsy54yaphl1p">
            <label htmlFor="what" className="block text-sm font-medium text-slate-900" data-id="element-219" data-mp="r04y23rrm0mg">
              What is wrong? <span className="font-normal text-slate-400" data-id="element-220" data-mp="3aiznij7vpfi">required</span>
            </label>
            <textarea id="what" rows={3} value={what} onChange={e => setWhat(e.target.value)} placeholder="One sentence." className="mt-2 w-full rounded-lg border border-slate-300 p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200" data-id="element-221" data-mp="3vebhkhm82vpb" />
          </div>

          <fieldset data-id="element-222" data-mp="qnkp6iv2d05c">
            <legend className="text-sm font-medium text-slate-900" data-id="element-223" data-mp="qnul0n2si1iy">
              Which source establishes this?
            </legend>
            <div className="mt-2 space-y-1.5" data-id="element-224" data-mp="vo8hd7v13n15">
              {documentOptions.map(opt => <label key={opt.id} className="flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-200 px-3 py-2 text-[13px] text-slate-700 transition-colors hover:border-slate-300" data-id="element-225" data-mp="zpu0vc22izkb">
                  <input type="checkbox" checked={sources.includes(opt.id)} onChange={() => toggleSource(opt.id)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400" data-id="element-226" data-mp="36f8ogkxjv4rb" />
                  <span data-id="element-227" data-mp="2z210xrxsaqku">{opt.label}</span>
                </label>)}
            </div>
          </fieldset>

          <fieldset data-id="element-228" data-mp="2u4hbdvvmyrdl">
            <legend className="text-sm font-medium text-slate-900" data-id="element-229" data-mp="2u1v848fs2y16">Proposed action</legend>
            <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-230" data-mp="2p6piozn6318c">
              {ACTIONS.map(a => <button key={a.id} type="button" onClick={() => setActionType(a.id)} className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${actionType === a.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`} data-id="element-231" data-mp="3mrvc852gg72z">
                  <span className="block text-[13px] font-medium" data-id="element-232" data-mp="1wf5ut2fmkz00">{a.label}</span>
                  <span className={`block text-[11px] ${actionType === a.id ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-233" data-mp="og8hokxftler">
                    {a.hint}
                  </span>
                </button>)}
            </div>

            {needsEntry && <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3" data-id="element-234" data-mp="jfubz9jb6njw">
                <div data-id="element-235" data-mp="4r7xtb5f41u9">
                  <label htmlFor="dr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-236" data-mp="4qoepr4177en">
                    Debit account
                  </label>
                  <input id="dr" value={debit} onChange={e => setDebit(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-237" data-mp="3rzbm0nj34lfo" />
                </div>
                <div data-id="element-238" data-mp="33cca33amh4v6">
                  <label htmlFor="cr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-239" data-mp="33bu6fllmrod8">
                    Credit account
                  </label>
                  <input id="cr" value={credit} onChange={e => setCredit(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-240" data-mp="3dai839tb5qur" />
                </div>
                <div data-id="element-241" data-mp="3i9d3rccdj9wa">
                  <label htmlFor="amt" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-242" data-mp="3i7s79h6i1gvs">
                    Amount (EUR)
                  </label>
                  <input id="amt" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 font-mono-num text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-243" data-mp="3r2q1qspap1p8" />
                </div>
                <div className="col-span-2" data-id="element-244" data-mp="2qmliaekkbug1">
                  <label htmlFor="narr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-245" data-mp="2qla4dlxel6b7">
                    Narrative
                  </label>
                  <textarea id="narr" rows={2} value={narrative} onChange={e => setNarrative(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-246" data-mp="30jzozmgop4v5" />
                </div>
              </div>}

            {actionType === 'escalate' && <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3" data-id="element-247" data-mp="1k7asubbaamz0">
                <div data-id="element-248" data-mp="1k4yc21h9iinj">
                  <label htmlFor="to" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-249" data-mp="1p5vv0ga3jgq3">
                    Escalate to
                  </label>
                  <input id="to" value={escalateTo} onChange={e => setEscalateTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-250" data-mp="68oo24m0btuj" />
                </div>
                <div data-id="element-251" data-mp="3jikyt84lgdip">
                  <label htmlFor="why" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-252" data-mp="3jj4a45zqo3nr">
                    Why
                  </label>
                  <textarea id="why" rows={2} value={escalateWhy} onChange={e => setEscalateWhy(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-253" data-mp="3aqapdt1iwc2i" />
                </div>
              </div>}
          </fieldset>

          <fieldset data-id="element-254" data-mp="382idphoc4jen">
            <legend className="text-sm font-medium text-slate-900" data-id="element-255" data-mp="335jmb6w22qk5">
              Would you sign off on this line as it now stands?
            </legend>
            <div className="mt-2 flex gap-2" data-id="element-256" data-mp="3gvn3vkjzpw39">
              {SIGN_OFFS.map(s => <button key={s.id} type="button" onClick={() => setSignOff(s.id)} className={`flex-1 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${signOff === s.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`} data-id="element-257" data-mp="3cwea4foy9y7p">
                  {s.label}
                </button>)}
            </div>
          </fieldset>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4" data-id="element-258" data-mp="8g3e4vij2vsy">
          {existing ? <button type="button" onClick={() => {
          onDelete();
          onClose();
        }} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 transition-colors hover:text-red-600" data-id="element-259" data-mp="h0i2731ic8l0">
              <Trash2Icon className="h-3.5 w-3.5" data-id="element-260" data-mp="c0vpqvti6wd9" />
              Remove entry
            </button> : <span data-id="element-261" data-mp="14nq4rd7jx3jc" />}
          <div className="flex items-center gap-2" data-id="element-262" data-mp="14nzq2nhnimip">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 transition-colors hover:border-slate-400" data-id="element-263" data-mp="14pj4s9nnspid">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={!canSave} className="rounded-md bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-264" data-mp="1ilo7tmgf9djj">
              Save
            </button>
          </div>
        </footer>
      </aside>
    </div>;
}